import React, { useState, useEffect } from "react";
import { adminApi } from "../../api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Plus,
  Trash2,
  Edit,
  ExternalLink,
  Star,
  Package,
  ChevronDown,
  ChevronUp,
  Sparkles,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [niches, setNiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNiche, setSelectedNiche] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    niche_id: "",
    name: "",
    slug: "",
    logo_url: "",
    website_url: "",
    affiliate_link: "",
    tagline: "",
    description: "",
    best_for: "",
    rating: 4.5,
    starting_price: "",
    has_free_tier: false,
    primary_metric: "",
    primary_metric_value: "",
    unique_feature_1: "",
    unique_feature_1_value: "",
    unique_feature_2: "",
    unique_feature_2_value: "",
    integrations: "",
    pros: "",
    cons: "",
    is_featured: false,
    rank_order: 0,
  });

  const fetchData = async () => {
    try {
      const [nichesRes, productsRes] = await Promise.all([
        adminApi.getNiches(),
        adminApi.getProducts(selectedNiche === "all" ? null : selectedNiche),
      ]);
      setNiches(nichesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedNiche]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "name" && {
        slug: value.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      }),
    }));
  };

  const resetForm = () => {
    setFormData({
      niche_id: niches[0]?.id || "",
      name: "",
      slug: "",
      logo_url: "",
      website_url: "",
      affiliate_link: "",
      tagline: "",
      description: "",
      best_for: "",
      rating: 4.5,
      starting_price: "",
      has_free_tier: false,
      primary_metric: "",
      primary_metric_value: "",
      unique_feature_1: "",
      unique_feature_1_value: "",
      unique_feature_2: "",
      unique_feature_2_value: "",
      integrations: "",
      pros: "",
      cons: "",
      is_featured: false,
      rank_order: 0,
    });
    setEditingProduct(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        rating: parseFloat(formData.rating) || 0,
        rank_order: parseInt(formData.rank_order) || 0,
        integrations: formData.integrations
          ? formData.integrations.split(",").map((s) => s.trim())
          : [],
        pros: formData.pros
          ? formData.pros.split("\n").filter((s) => s.trim())
          : [],
        cons: formData.cons
          ? formData.cons.split("\n").filter((s) => s.trim())
          : [],
      };

      if (editingProduct) {
        await adminApi.updateProduct(editingProduct.id, payload);
        toast.success("Product updated!");
      } else {
        await adminApi.createProduct(payload);
        toast.success("Product created!");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(editingProduct ? "Failed to update" : "Failed to create");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      niche_id: product.niche_id,
      name: product.name,
      slug: product.slug,
      logo_url: product.logo_url || "",
      website_url: product.website_url,
      affiliate_link: product.affiliate_link,
      tagline: product.tagline,
      description: product.description,
      best_for: product.best_for,
      rating: product.rating,
      starting_price: product.starting_price || "",
      has_free_tier: product.has_free_tier,
      primary_metric: product.primary_metric || "",
      primary_metric_value: product.primary_metric_value || "",
      unique_feature_1: product.unique_feature_1 || "",
      unique_feature_1_value: product.unique_feature_1_value || "",
      unique_feature_2: product.unique_feature_2 || "",
      unique_feature_2_value: product.unique_feature_2_value || "",
      integrations: product.integrations?.join(", ") || "",
      pros: product.pros?.join("\n") || "",
      cons: product.cons?.join("\n") || "",
      is_featured: product.is_featured,
      rank_order: product.rank_order,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await adminApi.deleteProduct(id);
      toast.success("Product deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="products-loading">
        <div className="animate-pulse text-[#A1A1AA]">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Products</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">
            Manage affiliate products across all niches
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedNiche}
            onChange={(e) => setSelectedNiche(e.target.value)}
            className="bg-[#1A1A1A] border border-white/10 text-white rounded-md px-3 py-2 text-sm"
            data-testid="filter-niche-select"
          >
            <option value="all">All Niches</option>
            {niches.map((niche) => (
              <option key={niche.id} value={niche.id}>
                {niche.name}
              </option>
            ))}
          </select>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#007AFF] hover:bg-[#007AFF]/80 text-white"
                data-testid="add-product-btn"
              >
                <Plus size={16} className="mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingProduct ? "Edit Product" : "Add New Product"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-[#A1A1AA]">Niche *</label>
                    <select
                      name="niche_id"
                      value={formData.niche_id}
                      onChange={handleInputChange}
                      className="w-full bg-[#1A1A1A] border border-white/10 text-white rounded-md p-2 mt-1"
                      required
                      data-testid="product-niche-select"
                    >
                      <option value="">Select niche</option>
                      {niches.map((niche) => (
                        <option key={niche.id} value={niche.id}>
                          {niche.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-[#A1A1AA]">Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="HeyGen"
                      className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                      required
                      data-testid="product-name-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-[#A1A1AA]">Slug *</label>
                    <Input
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      placeholder="heygen"
                      className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                      required
                      data-testid="product-slug-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#A1A1AA]">Best For *</label>
                    <Input
                      name="best_for"
                      value={formData.best_for}
                      onChange={handleInputChange}
                      placeholder="Content Creators"
                      className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                      required
                      data-testid="product-best-for-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-[#A1A1AA]">Tagline *</label>
                  <Input
                    name="tagline"
                    value={formData.tagline}
                    onChange={handleInputChange}
                    placeholder="Create studio-quality videos with AI avatars"
                    className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                    required
                    data-testid="product-tagline-input"
                  />
                </div>

                <div>
                  <label className="text-sm text-[#A1A1AA]">Description *</label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="HeyGen is an AI video generation platform..."
                    className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                    rows={3}
                    required
                    data-testid="product-description-input"
                  />
                </div>

                {/* URLs */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-[#A1A1AA]">Website URL *</label>
                    <Input
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleInputChange}
                      placeholder="https://heygen.com"
                      className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                      required
                      data-testid="product-website-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#A1A1AA]">Affiliate Link *</label>
                    <Input
                      name="affiliate_link"
                      value={formData.affiliate_link}
                      onChange={handleInputChange}
                      placeholder="https://heygen.com?ref=..."
                      className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                      required
                      data-testid="product-affiliate-link-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-[#A1A1AA]">Logo URL</label>
                  <Input
                    name="logo_url"
                    value={formData.logo_url}
                    onChange={handleInputChange}
                    placeholder="https://..."
                    className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                    data-testid="product-logo-input"
                  />
                </div>

                {/* Pricing */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-[#A1A1AA]">Starting Price</label>
                    <Input
                      name="starting_price"
                      value={formData.starting_price}
                      onChange={handleInputChange}
                      placeholder="$24/mo"
                      className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                      data-testid="product-price-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#A1A1AA]">Rating (0-5)</label>
                    <Input
                      name="rating"
                      type="number"
                      min="0"
                      max="5"
                      step="0.1"
                      value={formData.rating}
                      onChange={handleInputChange}
                      className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                      data-testid="product-rating-input"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 p-2">
                      <input
                        type="checkbox"
                        name="has_free_tier"
                        checked={formData.has_free_tier}
                        onChange={handleInputChange}
                        className="rounded"
                        data-testid="product-free-tier-checkbox"
                      />
                      <span className="text-sm text-[#A1A1AA]">Free Tier</span>
                    </label>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
                  <h4 className="text-sm font-semibold text-white">Key Features (for comparison)</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      name="primary_metric"
                      value={formData.primary_metric}
                      onChange={handleInputChange}
                      placeholder="Primary Metric (e.g., Video Minutes)"
                      className="bg-[#121212] border-white/10 text-white"
                      data-testid="product-primary-metric-input"
                    />
                    <Input
                      name="primary_metric_value"
                      value={formData.primary_metric_value}
                      onChange={handleInputChange}
                      placeholder="Value (e.g., 15 mins/mo)"
                      className="bg-[#121212] border-white/10 text-white"
                      data-testid="product-primary-metric-value-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      name="unique_feature_1"
                      value={formData.unique_feature_1}
                      onChange={handleInputChange}
                      placeholder="Feature 1 (e.g., Avatar Quality)"
                      className="bg-[#121212] border-white/10 text-white"
                    />
                    <Input
                      name="unique_feature_1_value"
                      value={formData.unique_feature_1_value}
                      onChange={handleInputChange}
                      placeholder="Value (e.g., Ultra-realistic)"
                      className="bg-[#121212] border-white/10 text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      name="unique_feature_2"
                      value={formData.unique_feature_2}
                      onChange={handleInputChange}
                      placeholder="Feature 2 (e.g., Languages)"
                      className="bg-[#121212] border-white/10 text-white"
                    />
                    <Input
                      name="unique_feature_2_value"
                      value={formData.unique_feature_2_value}
                      onChange={handleInputChange}
                      placeholder="Value (e.g., 40+ supported)"
                      className="bg-[#121212] border-white/10 text-white"
                    />
                  </div>
                  <Input
                    name="integrations"
                    value={formData.integrations}
                    onChange={handleInputChange}
                    placeholder="Integrations (comma-separated: Zapier, Canva, API)"
                    className="bg-[#121212] border-white/10 text-white"
                    data-testid="product-integrations-input"
                  />
                </div>

                {/* Pros & Cons */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-[#A1A1AA]">Pros (one per line)</label>
                    <Textarea
                      name="pros"
                      value={formData.pros}
                      onChange={handleInputChange}
                      placeholder="Ultra-realistic avatars&#10;Easy to use&#10;Fast rendering"
                      className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                      rows={4}
                      data-testid="product-pros-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#A1A1AA]">Cons (one per line)</label>
                    <Textarea
                      name="cons"
                      value={formData.cons}
                      onChange={handleInputChange}
                      placeholder="Premium pricing&#10;Limited free tier"
                      className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                      rows={4}
                      data-testid="product-cons-input"
                    />
                  </div>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-[#A1A1AA]">Rank Order</label>
                    <Input
                      name="rank_order"
                      type="number"
                      value={formData.rank_order}
                      onChange={handleInputChange}
                      className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                      data-testid="product-rank-input"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 p-2">
                      <input
                        type="checkbox"
                        name="is_featured"
                        checked={formData.is_featured}
                        onChange={handleInputChange}
                        className="rounded"
                        data-testid="product-featured-checkbox"
                      />
                      <span className="text-sm text-[#A1A1AA]">Featured Product</span>
                    </label>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#007AFF] hover:bg-[#007AFF]/80"
                  data-testid="save-product-btn"
                >
                  {editingProduct ? "Update Product" : "Create Product"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Products List */}
      {products.length === 0 ? (
        <div className="bg-[#121212] border border-white/10 rounded-lg p-12 text-center">
          <Package size={48} className="mx-auto text-[#A1A1AA] mb-4" />
          <h3 className="text-white text-lg font-semibold">No products yet</h3>
          <p className="text-[#A1A1AA] mt-2">
            Add your first affiliate product to get started
          </p>
        </div>
      ) : (
        <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#1A1A1A] border-b border-white/10">
              <tr>
                <th className="text-left text-[#A1A1AA] text-xs font-mono uppercase tracking-wider p-4">
                  Product
                </th>
                <th className="text-left text-[#A1A1AA] text-xs font-mono uppercase tracking-wider p-4">
                  Rating
                </th>
                <th className="text-left text-[#A1A1AA] text-xs font-mono uppercase tracking-wider p-4">
                  Price
                </th>
                <th className="text-left text-[#A1A1AA] text-xs font-mono uppercase tracking-wider p-4">
                  Status
                </th>
                <th className="text-right text-[#A1A1AA] text-xs font-mono uppercase tracking-wider p-4">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {products.map((product) => (
                <tr
                  key={product.id}
                  className="hover:bg-white/5 transition-colors"
                  data-testid={`product-row-${product.slug}`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {product.logo_url ? (
                        <img
                          src={product.logo_url}
                          alt={product.name}
                          className="w-10 h-10 rounded-md object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-md bg-[#007AFF]/20 flex items-center justify-center text-[#007AFF] font-bold">
                          {product.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <p className="text-white font-medium">{product.name}</p>
                        <p className="text-[#A1A1AA] text-sm">{product.best_for}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1">
                      <Star size={14} className="text-yellow-400 fill-yellow-400" />
                      <span className="text-white font-mono">
                        {product.rating.toFixed(1)}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-white font-mono">
                      {product.starting_price || "N/A"}
                    </span>
                    {product.has_free_tier && (
                      <span className="ml-2 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                        Free tier
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {product.is_featured && (
                        <span className="text-xs bg-[#007AFF]/20 text-[#007AFF] px-2 py-0.5 rounded">
                          Featured
                        </span>
                      )}
                      {product.ai_review && (
                        <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded flex items-center gap-1">
                          <Sparkles size={10} />
                          AI Content
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(product)}
                        className="p-2 text-[#A1A1AA] hover:text-white rounded-md hover:bg-white/10 transition-colors"
                        data-testid={`edit-product-${product.slug}-btn`}
                      >
                        <Edit size={16} />
                      </button>
                      <a
                        href={`/${product.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-[#A1A1AA] hover:text-white rounded-md hover:bg-white/10 transition-colors"
                      >
                        <ExternalLink size={16} />
                      </a>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="p-2 text-[#A1A1AA] hover:text-red-400 rounded-md hover:bg-white/10 transition-colors"
                        data-testid={`delete-product-${product.slug}-btn`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
